import { useCallback, useEffect, useState } from "react";
import type {
  GemMaterialParams,
  GemUniformName,
  GroupInfo,
  MetalMaterialParams,
  MetalParamName,
  Vec3,
} from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState, useEditorStore } from "../../../../state/editor/useEditor";
import { createPreset, deletePreset, listPresets, type Preset } from "../../../../features/presets/presetsApi";
import { promptDialog, useToast } from "../../../../shared/ui";
import { Icon } from "../../../kit";
import { MiniBtn } from "../../inspector/rows";

/** The metal fields copied by an apply (color is set separately). */
const METAL_PARAMS: MetalParamName[] = [
  "roughness", "metalness", "envMapIntensity", "ior",
  "clearcoat", "clearcoatRoughness", "specularIntensity", "sheen", "anisotropy",
];

/** Gem uniforms copied on apply (color/boostFactors/opacity are set separately). */
const GEM_UNIFORMS: GemUniformName[] = [
  "bounces", "ior", "dispersion", "reflectivity", "envMapIntensity", "sparkleBoost",
  "absorptionFactor", "gammaFactor", "squashFactor", "geometryFactor", "envRotation",
  "surfaceReflBoost", "internalEnvBoost",
];

/** Env/light snapshot persisted by an "environment" preset. Everything here has
 *  a live getter AND setter; the HDR image selection has no getter, so it is
 *  deliberately not captured rather than captured wrong. */
interface EnvSnapshot {
  background: string | null;
  backgroundIntensity: number;
  envIntensity: number;
  envRotation: Vec3;
  showEnvAsBackground: boolean;
  backgroundBlur: number;
  toneMappingExposure: number;
  lightMultiplier: number;
  keyLightIntensity: number;
  keyLightColor: string;
  keyLightDirection: Vec3;
  fillLightIntensity: number;
  fillLightColor: string;
  fillLightDirection: Vec3;
  hemiLightIntensity: number;
  hemiSkyColor: string;
  hemiGroundColor: string;
}

type Kind = "metal" | "gem" | "env";
const LABEL: Record<Kind, string> = { metal: "Metal", gem: "Stone", env: "Environment" };

/**
 * Only VALUE snapshots belong in this section — what `save()` below writes.
 *
 * `listPresets` returns system ∪ workspace, and the same kinds also hold
 * file-backed presets: curated library files (a `.pmat`, an `.hdr`) and, since
 * 2026-10-31, HDR Studio's saved environments (`values.source === "hdr-studio"`,
 * `fileUrl` = the baked map). Neither carries the fields `apply` reads —
 * `values.envRotation.x` on an HDR Studio row was a TypeError on click. Those
 * presets are applied by the library grids (HDRIs, Metal HDR, Gem HDR), which
 * load their file; here they are skipped.
 */
export function isValueSnapshotPreset(p: Pick<Preset, "fileUrl" | "values">): boolean {
  if (p.fileUrl) return false;
  const v = p.values as Record<string, unknown> | null | undefined;
  if (!v || typeof v !== "object") return false;
  return v.source !== "hdr-studio";
}

/**
 * Workspace-saved material and environment presets.
 *
 * Ported from the Canvas chrome, which was the only surface that could WRITE
 * these. The right rail's Material Library reads curated and built-in presets
 * and can apply them; it has never been able to save what is on screen, and
 * never had the fan-out ("apply to every metal part") that makes a saved metal
 * useful on a multi-part model.
 *
 * ⚠️ Preset documents of kind metal/gem/env already exist in workspaces that
 * used the Canvas chrome. This section is what makes them reachable again.
 */
export function WorkspacePresets({ kind }: { kind: Kind }) {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const { toast } = useToast();
  const selection = useDomainState("selection");

  const [items, setItems] = useState<Preset[]>([]);
  const [parts, setParts] = useState<GroupInfo[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      setItems((await listPresets(kind)).filter(isValueSnapshotPreset));
    } catch {
      // Unauthenticated (/dev/studio) or the API is down. An empty chip row is
      // the honest rendering of "nothing saved here"; an error banner for a
      // feature the user never invoked is not.
      setItems([]);
    }
  }, [kind]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    if (kind === "env") return;
    void r.listGroups().then(setParts).catch(() => setParts([]));
  }, [r, kind, selection]);

  const save = async () => {
    let values: Record<string, unknown>;
    if (kind === "env") {
      const s = await r.getSceneState();
      values = {
        background: s.background, backgroundIntensity: s.backgroundIntensity,
        envIntensity: s.envIntensity, envRotation: s.envRotation,
        showEnvAsBackground: s.showEnvAsBackground, backgroundBlur: s.backgroundBlur,
        toneMappingExposure: s.toneMappingExposure, lightMultiplier: s.lightMultiplier,
        keyLightIntensity: s.keyLightIntensity, keyLightColor: s.keyLightColor, keyLightDirection: s.keyLightDirection,
        fillLightIntensity: s.fillLightIntensity, fillLightColor: s.fillLightColor, fillLightDirection: s.fillLightDirection,
        hemiLightIntensity: s.hemiLightIntensity, hemiSkyColor: s.hemiSkyColor, hemiGroundColor: s.hemiGroundColor,
      } satisfies EnvSnapshot;
    } else {
      const p = selection ? await r.getGroupMaterialParams(selection) : null;
      if (!p || p.kind !== kind) {
        toast({
          tone: "warning",
          title: `Select a ${LABEL[kind].toLowerCase()} part first`,
          description: `Pick a ${LABEL[kind].toLowerCase()} part, then save its look as a preset.`,
        });
        return;
      }
      values = p as unknown as Record<string, unknown>;
    }
    const name = await promptDialog({
      title: `Name this ${LABEL[kind].toLowerCase()} preset`,
      label: "Name",
      defaultValue: `${LABEL[kind]} ${items.length + 1}`,
    });
    if (!name) return;
    setBusy(true);
    try {
      await createPreset({ kind, name, values });
      await reload();
      toast({ tone: "success", title: `${LABEL[kind]} preset saved` });
    } catch {
      toast({ tone: "error", title: "Could not save preset" });
    } finally {
      setBusy(false);
    }
  };

  /** Apply to the selected part of this kind, or — when nothing matching is
   *  selected — to EVERY part of this kind. That fan-out is the whole reason a
   *  saved metal is worth having on a multi-part model. */
  const apply = async (preset: Preset) => {
    if (kind === "env") {
      const v = preset.values as unknown as EnvSnapshot;
      r.setBackground(v.background);
      r.setBackgroundIntensity(v.backgroundIntensity);
      r.setEnvIntensity(v.envIntensity);
      r.setEnvRotation(v.envRotation.x, v.envRotation.y, v.envRotation.z);
      r.setShowEnvAsBackground(v.showEnvAsBackground);
      r.setBackgroundBlur(v.backgroundBlur);
      r.setToneMappingExposure(v.toneMappingExposure);
      r.setLightMultiplier(v.lightMultiplier);
      r.setKeyLightIntensity(v.keyLightIntensity);
      r.setKeyLightColor(v.keyLightColor);
      r.setKeyLightDirection(v.keyLightDirection.x, v.keyLightDirection.y, v.keyLightDirection.z);
      r.setFillLightIntensity(v.fillLightIntensity);
      r.setFillLightColor(v.fillLightColor);
      r.setFillLightDirection(v.fillLightDirection.x, v.fillLightDirection.y, v.fillLightDirection.z);
      r.setHemiLightIntensity(v.hemiLightIntensity);
      r.setHemiSkyColor(v.hemiSkyColor);
      r.setHemiGroundColor(v.hemiGroundColor);
      await store.refresh("scene");
      toast({ tone: "success", title: `Applied “${preset.name}”` });
      return;
    }

    const sel = selection ? parts.find((p) => p.id === selection && p.kind === kind) : null;
    const targets = sel ? [sel] : parts.filter((p) => p.kind === kind);
    if (targets.length === 0) {
      toast({ tone: "warning", title: `No ${LABEL[kind].toLowerCase()} parts to apply to` });
      return;
    }
    if (kind === "metal") {
      const v = preset.values as unknown as MetalMaterialParams;
      for (const g of targets) {
        r.setGroupMetalColor(g.id, v.color);
        for (const name of METAL_PARAMS) r.setGroupMetalParam(g.id, name, v[name]);
      }
    } else {
      const v = preset.values as unknown as GemMaterialParams;
      const axes = ["x", "y", "z"] as const;
      for (const g of targets) {
        for (const name of GEM_UNIFORMS) r.setGroupGemUniform(g.id, name, v[name]);
        axes.forEach((ax, i) => r.setGroupGemVec3Component(g.id, "color", ax, v.color[i]));
        axes.forEach((ax, i) => r.setGroupGemVec3Component(g.id, "boostFactors", ax, v.boostFactors[i]));
        r.setGroupGemOpacity(g.id, v.opacity);
      }
    }
    await store.refresh("groups");
    if (selection) void r.getGroupMaterialParams(selection).then((p) => store.setState("material", p));
    toast({
      tone: "success",
      title: `Applied “${preset.name}”`,
      description: targets.length > 1 ? `To all ${targets.length} ${LABEL[kind].toLowerCase()} parts.` : undefined,
    });
  };

  const remove = async (p: Preset) => {
    try {
      await deletePreset(p._id);
      await reload();
    } catch {
      toast({ tone: "error", title: "Could not delete that preset" });
    }
  };

  const fanOut = kind !== "env" && !parts.some((p) => p.id === selection && p.kind === kind);

  return (
    <div className="flex flex-col gap-2">
      <MiniBtn icon="bookmark_add" busy={busy} onClick={() => void save()}>
        Save as {LABEL[kind].toLowerCase()} preset
      </MiniBtn>

      {items.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((p) => (
            <span
              key={p._id}
              className="group flex max-w-full items-center gap-1 rounded-full border border-ed-border bg-white pl-2.5 pr-1 text-[12px] font-medium text-ed-text-2 transition-colors hover:border-ed-accent"
            >
              <button type="button" title={`Apply “${p.name}”`} onClick={() => void apply(p)} className="truncate py-1">
                {p.name}
              </button>
              <button
                type="button"
                title={`Delete “${p.name}”`}
                onClick={() => void remove(p)}
                className="flex h-4 w-4 items-center justify-center rounded-full text-ed-text-3 opacity-0 transition-opacity hover:text-ed-red group-hover:opacity-100"
              >
                <Icon name="close" size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <span className="text-[11px] text-ed-text-3">Nothing saved in this workspace yet.</span>
      )}

      {/* Say which way an apply will go BEFORE it goes, since the fan-out is
          silent and touches every part of the kind. */}
      {items.length > 0 && kind !== "env" ? (
        <span className="text-[11px] leading-snug text-ed-text-3">
          {fanOut
            ? `Applies to every ${LABEL[kind].toLowerCase()} part — select one to apply to it alone.`
            : "Applies to the selected part."}
        </span>
      ) : null}
    </div>
  );
}
